import { LEAD_CAPTURE } from '@/composables/useLeadCaptureTabs'
import { WIZARD_PATH as FULFILLMENT_METHOD_WIZARD_PATH } from '@/config/fulfillmentWizardSteps'
import { capped, isNavLinkActive, isSectionPath, VISIBLE_NESTED_ITEMS, withOverview } from '@/config/nav/helpers'
import { lockedPath } from '@/helpers/entitlements'
import { PARSLEY_PATH } from '@/helpers/parsley'

export { isNavLinkActive, VISIBLE_NESTED_ITEMS }

export const SELL_BASES = ['/sell', '/store', '/storefront']

export const isSellPath = path => isSectionPath(path, SELL_BASES)

const MEMBERSHIP_TIER_WIZARD_PATH = '/store/membership-tiers/setup'
const MEMBERSHIP_TIERS = 'Membership Tiers'
const FULFILLMENT_METHODS = 'Fulfillment Methods'
const SITE_SETTINGS = 'Site Settings'

export const HIDDEN_FOR_CATERING = ['Drops', MEMBERSHIP_TIERS, FULFILLMENT_METHODS, SITE_SETTINGS]

const CATERING_SWAPS = {
  Stores: { name: 'Manage Menu Page', link: '/sell', match: ['/sell'] },
  Products: { name: 'Products', link: '/store/products', match: ['/store/products'] }
}

// Returns the item as is when the feature is on, a locked stub when the
// feature is shown locked, and nothing when it is hidden.
const gated = (key, featureState, item) => {
  const state = featureState(key)
  if (state === 'on') return [item]
  if (state === 'locked') return [{ name: item.name, link: lockedPath(key), match: [lockedPath(key)], locked: true }]
  return []
}

const byOrder = (a, b) => a.attributes.order - b.attributes.order

const draftItems = (name, link) => (name ? [{ name, link, match: [link], badge: 'Draft' }] : [])

const withDraft = (list, draft) => ({ ...list, children: [...list.children, ...draft] })

export function buildSellNavItems({
  isCatering = false,
  stores = [],
  fulfillmentMethods = [],
  membershipTiers = [],
  expanded = [],
  revealed = false,
  parsley = false,
  draftTier = null,
  draftMethod = null,
  featureState = () => 'on',
  path
} = {}) {
  const tierItems = capped(
    membershipTiers.map(tier => ({
      name: tier.attributes.tier_name || `Tier #${tier.id}`,
      link: `/store/membership-tiers/${tier.id}`,
      match: [`/store/membership-tiers/${tier.id}`]
    })),
    expanded.includes(MEMBERSHIP_TIERS),
    path
  )
  const items = [
    {
      name: 'Stores',
      link: '/store/stores',
      match: ['/sell', '/store/stores'],
      children: [
        { name: 'All stores', link: '/store/stores', exact: ['/sell', '/store/stores'] },
        ...stores
          .slice()
          .sort(byOrder)
          .map(store => ({
            name: store.attributes.store_name,
            link: `/store/stores/${store.id}`,
            match: [`/store/stores/${store.id}`]
          }))
      ]
    },
    { name: 'Drops', link: '/store/drops', match: ['/store/drops'] },
    {
      name: 'Products',
      link: '/store/products',
      match: ['/store/products', '/store/folders', '/store/labels', '/store/packing-slips', PARSLEY_PATH],
      children: [
        { name: 'Library', link: '/store/products', match: ['/store/products'] },
        { name: 'Folders', link: '/store/folders' },
        { name: 'Labels', link: '/store/labels', match: ['/store/labels'] },
        { name: 'Packing slip settings', link: '/store/packing-slips' },
        ...(parsley ? [{ name: 'Parsley', link: PARSLEY_PATH, match: [PARSLEY_PATH] }] : [])
      ]
    },
    {
      name: 'Discount Codes',
      link: '/store/discount-codes',
      match: ['/store/discount-codes'],
      children: [
        { name: 'Overview', link: '/store/discount-codes' },
        {
          name: 'Manage discount codes',
          link: '/store/discount-codes/manage',
          match: ['/store/discount-codes'],
          exclude: ['/store/discount-codes']
        }
      ]
    },
    { name: 'Account Credits', link: '/store/account-credits', match: ['/store/account-credits'] },
    ...gated('membership_program', featureState, {
      name: MEMBERSHIP_TIERS,
      link: '/store/membership-tiers',
      match: ['/store/membership-tiers'],
      ...withOverview(
        { name: 'Overview', link: '/store/membership-tiers' },
        withDraft(tierItems, draftItems(draftTier, MEMBERSHIP_TIER_WIZARD_PATH))
      )
    }),
    ...gated('loyalty', featureState, {
      name: 'Loyalty Program',
      link: '/store/loyalty-program',
      match: ['/store/loyalty-program'],
      children: [
        { name: 'Overview', link: '/store/loyalty-program' },
        { name: 'Settings', link: '/store/loyalty-program/settings' }
      ]
    }),
    ...gated('referrals', featureState, {
      name: 'Referral Program',
      link: '/store/referral-program',
      match: ['/store/referral-program'],
      children: [
        { name: 'Overview', link: '/store/referral-program' },
        { name: 'Referral codes', link: '/store/referral-program/codes', match: ['/store/referral-program/codes'] },
        { name: 'Settings', link: '/store/referral-program/settings' }
      ]
    }),
    {
      name: FULFILLMENT_METHODS,
      link: '/store/fulfillment-methods',
      match: ['/store/fulfillment-methods'],
      ...withOverview(
        { name: 'Overview', link: '/store/fulfillment-methods' },
        withDraft(
          capped(
            fulfillmentMethods.map(method => ({
              name: method.attributes.name,
              link: `/store/fulfillment-methods/${method.id}`,
              match: [`/store/fulfillment-methods/${method.id}`]
            })),
            expanded.includes(FULFILLMENT_METHODS),
            path
          ),
          draftItems(draftMethod, FULFILLMENT_METHOD_WIZARD_PATH)
        )
      )
    },
    ...gated('website_builder', featureState, {
      name: SITE_SETTINGS,
      link: '/storefront',
      match: ['/storefront'],
      children: [
        { name: 'Configuration', link: '/storefront' },
        { name: 'Content blocks', link: '/storefront/modules' },
        { name: 'Links menu', link: '/storefront/links' },
        ...(revealed ? [{ name: 'Lead capture', link: LEAD_CAPTURE, match: [LEAD_CAPTURE] }] : [])
      ]
    })
  ]

  if (!isCatering) return items
  return items.filter(item => !HIDDEN_FOR_CATERING.includes(item.name)).map(item => CATERING_SWAPS[item.name] ?? item)
}
